'use client';

import { useRef, type ReactNode } from 'react';
import { motion, useInView, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { clsx } from 'clsx';

// ── The site line ─────────────────────────────────────────────────────────
// A laser-level line that draws itself down the left gutter as you scroll,
// linking the four build stages (electrical → tile → painting → floors) and
// running on through About/Guarantees to end at the estimate form — the same
// order the real renovation (and its staged payments) follows. Numbered
// markers (<StageMarker/>, rendered by each ServiceSection) fill as each
// stage is reached. Desktop only — the mobile gutter is too narrow. Reduced
// motion renders the line pre-drawn and the markers filled.

export default function StoryRail({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  // The tip of the line tracks the point 80% down the viewport, so it draws
  // just ahead of what's being read; the spring keeps the tip from jittering
  // on wheel steps.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.8', 'end 0.8'],
  });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 26, restDelta: 0.001 });
  // The laser dot rides the tip of the drawn line.
  const tipTop = useTransform(scaleY, (v) => `${Math.max(0, Math.min(1, v)) * 100}%`);

  return (
    <div ref={ref} className="relative">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-8 hidden -translate-x-1/2 lg:block"
      >
        {/* Track — the path ahead, barely there */}
        <div className="absolute inset-y-0 w-px bg-surface-bright/10" />
        {/* Fill — the drawn line (GPU: scaleY only) */}
        <motion.div
          className="absolute inset-y-0 w-px origin-top bg-primary/70"
          style={reduceMotion ? undefined : { scaleY }}
        />
        {/* Laser dot at the tip — the live point of the level line */}
        {!reduceMotion && (
          <motion.span
            className="absolute left-0 -translate-x-1/2 -translate-y-1/2"
            style={{ top: tipTop }}
          >
            <span className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/30 blur-[5px]" />
            <span className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" />
          </motion.span>
        )}
      </div>
      {children}
    </div>
  );
}

// One numbered stage dot, centred on the rail and aligned with the section's
// eyebrow. Fills (once) as the stage scrolls into the upper two-thirds of the
// viewport — "stage reached", mirroring the staged-payment model.
export function StageMarker({ n }: { n: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduceMotion = useReducedMotion();
  const reached = useInView(ref, { once: true, margin: '0px 0px -35% 0px' });
  const active = reduceMotion || reached;

  return (
    // Numbered node centred on the rail in the left gutter, aligned with the
    // section's eyebrow. Desktop only (lg:flex) — no marker on phones. bg-surface
    // keeps it opaque so the line doesn't run through the number.
    <span
      ref={ref}
      aria-hidden
      className={clsx(
        'absolute left-8 top-section-gap -mt-1.5 hidden h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border bg-surface font-headline text-[10px] font-semibold tracking-[0.08em] transition-colors duration-500 lg:flex',
        active ? 'border-primary text-primary' : 'border-outline-variant/60 text-outline'
      )}
    >
      {String(n).padStart(2, '0')}
    </span>
  );
}
